import { PageHeader } from '@/components/PageHeader';
import { CopyButton } from '@/components';
import { Button, Tooltip, TooltipTrigger, TooltipContent } from '@predoc/cow';
import { ArrowLeft, Hospital, Info } from 'lucide-react';
import { useNavigate, useParams } from 'react-router';
import { formatDate } from '@/utils/helpers';
import { EditProfileModal } from './EditProfileModal';
import { useGetOrgProfileById } from '../hooks/profilesAPI';
import { useListUsers } from '@/feature/users/hooks/usersApi';
import { OrgProfileUsersTable } from './OrgProfileUsersTable';
import { useState } from 'react';
import { PaginationState } from '@/components';
import { getProfileColor } from '@/utils/helpers';
import { getAvailableRequestActions } from '../utils/requestActions';
import { useGetOrganizationInfo } from '../hooks/organizationAPI';
import { ProfileDetailsSkeleton } from './ProfileDetailsSkeleton';
import { usePageTitle } from '@/hooks';

export const ProfileDetails = () => {
  const navigate = useNavigate();
  const { profileId } = useParams<{ profileId: string }>();
  const [pagination, setPagination] = useState<PaginationState>({
    page: 0,
    pageSize: 10,
  });

  const { data: profile, isLoading: isLoadingProfile } = useGetOrgProfileById(
    profileId!,
  );
  const { data: organizationInfo } = useGetOrganizationInfo();
  const { data: users, isFetching: isFetchingUsers } = useListUsers({
    organizationProfileIds: profileId ? [profileId] : undefined,
    pagination,
  });
  usePageTitle(profile?.displayName ?? '');

  const handleGoBack = () => {
    navigate(-1);
  };

  if (isLoadingProfile) {
    return <ProfileDetailsSkeleton />;
  }
  if (!profile) return null;

  // Transform users data for the table
  const tableUsers = (users?.data ?? []).map((user) => ({
    id: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
    role: user.role,
  }));

  const availableActions = getAvailableRequestActions({
    orgInfo: organizationInfo,
    organizationFacesheets: profile.facesheets,
    profileAvailableMethods: profile.availableRetrievalMethods,
    profileFacesheets: profile.facesheets,
  });
  const requestActionsDisplayNames = profile.defaultRetrievalMethods.map(
    (method) => {
      const action = availableActions.find((a) => a.name === method);
      return action ? action.displayName : method;
    },
  );

  const facesheetsDisplayNames = profile.facesheets
    .filter((f) => f.isDefault)
    .map((f) => f.displayName);

  const expeditedFacesheetsDisplayNames = profile.facesheets
    .filter((facesheet) => facesheet.isExpedited)
    .map((facesheet) => facesheet.displayName);

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader className="pl-4">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <Button variant="ghost" size="icon" onClick={handleGoBack}>
            <ArrowLeft className="size-4" />
          </Button>
          <div className="max-w-xl min-w-0 flex-1">
            <h6 className="text-muted-foreground text-sm">Profile</h6>
            <Tooltip>
              <TooltipTrigger asChild>
                <h1 className="truncate text-3xl font-bold">
                  {profile.displayName}
                </h1>
              </TooltipTrigger>
              <TooltipContent className="break-all">
                {profile.displayName}
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <EditProfileModal profile={profile} />
        </div>
      </PageHeader>

      <div className="mx-auto max-w-5xl px-4 py-8">
        <div className="rounded-lg border bg-white p-8">
          <div className="mb-10 grid grid-cols-2 gap-x-8 gap-y-8">
            <div className="min-w-0">
              <div className="mb-1 text-sm font-semibold">Profile Name</div>
              <div
                className={`break-all ${!profile.displayName ? 'text-gray-500' : ''}`}
              >
                {profile.displayName || '- None -'}
              </div>
            </div>
            <div>
              <div className="mb-1 text-sm font-semibold">Profile Icon</div>
              <div className={!profile.avatarType ? 'text-gray-500' : ''}>
                {profile.avatarType ? (
                  <div className="flex items-center gap-2">
                    <Hospital
                      className="size-6"
                      style={{ color: getProfileColor(profile.avatarType) }}
                    />
                  </div>
                ) : (
                  '- None -'
                )}
              </div>
            </div>
            <div>
              <div className="mb-1 text-sm font-semibold">Description</div>
              <div className={!profile.description ? 'text-gray-500' : ''}>
                {profile.description || '- None -'}
              </div>
            </div>
            <div className="space-y-8">
              <div>
                <div className="mb-1 flex items-center gap-1 text-sm font-semibold">
                  NPI # (Type 1)
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="text-muted-foreground size-4 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      NPI is managed by your Predoc Representative
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className={!profile.npiType1 ? 'text-gray-500' : ''}>
                  {profile.npiType1 ? (
                    <CopyButton
                      value={profile.npiType1}
                      successMessage="NPI number copied"
                    />
                  ) : (
                    '- Not Provided -'
                  )}
                </div>
              </div>
              <div>
                <div className="mb-1 flex items-center gap-1 text-sm font-semibold">
                  NPI # (Type 2)
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="text-muted-foreground size-4 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent>
                      NPI is managed by your Predoc Representative
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div className={!profile.npiType2 ? 'text-gray-500' : ''}>
                  {profile.npiType2 ? (
                    <CopyButton
                      value={profile.npiType2}
                      successMessage="NPI number copied"
                    />
                  ) : (
                    '- Not Provided -'
                  )}
                </div>
              </div>
            </div>
            <div>
              <div className="mb-1 text-sm font-semibold">Created By</div>
              <div>{profile.creatorName}</div>
            </div>
            <div>
              <div className="mb-1 text-sm font-semibold">Date Created</div>
              <div>{formatDate(profile.createdAt.toString())}</div>
            </div>
            <div>
              <div className="mb-1 text-sm font-semibold">
                Patients Assigned to Profile
              </div>
              <div>{profile.patientCount}</div>
            </div>
          </div>

          <div className="mb-10">
            <h2 className="mb-4 text-lg font-semibold">Advanced Settings</h2>
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              <div>
                <div className="mb-1 text-sm font-semibold">
                  Default Request Actions
                </div>
                {requestActionsDisplayNames.length > 0 ? (
                  <ul className="list-inside list-disc">
                    {requestActionsDisplayNames.map((displayName) => (
                      <li key={displayName}>{displayName}</li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-gray-500">- None -</div>
                )}
              </div>
              <div>
                <div className="mb-1 text-sm font-semibold">
                  Default Facesheets
                </div>
                <div
                  className={!profile.facesheets.length ? 'text-gray-500' : ''}
                >
                  {facesheetsDisplayNames.length
                    ? facesheetsDisplayNames.join(', ')
                    : '- None -'}
                </div>
                {organizationInfo?.enableFacesheetExpedited && (
                  <div className="mt-4">
                    <div className="mb-1 text-sm font-semibold">
                      Facesheets to Expedite
                    </div>
                    <div
                      className={
                        !profile.facesheets.length ? 'text-gray-500' : ''
                      }
                    >
                      {expeditedFacesheetsDisplayNames.length
                        ? expeditedFacesheetsDisplayNames.join(', ')
                        : '- None -'}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <OrgProfileUsersTable
            users={tableUsers}
            totalCount={users?.totalCount ?? 0}
            isLoading={isFetchingUsers}
            pagination={pagination}
            setPagination={setPagination}
          />
        </div>
      </div>
    </div>
  );
};
